import {
  FEATURE,
  canUseFeature,
  isProOrTeamPlan,
  isProPlan,
  isTeamPlan,
  normalizePlan,
} from '../config/planFeatures';

/** Notebook access modes for Learn workspaces. */

export const NOTEBOOK_ACCESS = {
  PUBLIC: 'public',
  PRIVATE: 'private',
  TEAM: 'team',
  EMAIL: 'email',
};

export { normalizePlan, isProPlan, isTeamPlan, isProOrTeamPlan };

/** Default selected mode on the Sharing step. */
export function defaultAccessModeForPlan(plan) {
  const p = normalizePlan(plan);
  if (canUseFeature(FEATURE.NOTEBOOK_PRIVATE, { plan: p })) {
    return NOTEBOOK_ACCESS.PRIVATE;
  }
  return NOTEBOOK_ACCESS.PUBLIC;
}

export function canSelectAccessMode(mode, plan) {
  if (mode === NOTEBOOK_ACCESS.PUBLIC) return true;
  if (mode === NOTEBOOK_ACCESS.PRIVATE) {
    return canUseFeature(FEATURE.NOTEBOOK_PRIVATE, { plan });
  }
  if (mode === NOTEBOOK_ACCESS.TEAM) {
    return canUseFeature(FEATURE.NOTEBOOK_SHARE_TEAM, { plan });
  }
  if (mode === NOTEBOOK_ACCESS.EMAIL) {
    return canUseFeature(FEATURE.NOTEBOOK_INVITE_EMAIL, { plan });
  }
  return false;
}

/** Clamp UI choice to what the user's plan allows before create/save. */
export function resolveAccessModeForPlan(plan, accessMode) {
  if (canSelectAccessMode(accessMode, plan)) return accessMode;
  return defaultAccessModeForPlan(plan);
}

/** Map API / entitlement errors to user-facing copy. */
export function formatSharingError(err) {
  const data = err?.response?.data;
  const raw =
    (typeof data === 'string' ? data : null)
    || data?.message
    || data?.detail
    || data?.error
    || err?.message
    || '';
  const code = String(raw).toUpperCase();
  if (code.includes('PRIVATE_NOTEBOOK_REQUIRES_UPGRADE')) {
    return 'Private notebooks require Pro or Team. Upgrade under Settings → Billing.';
  }
  if (code.includes('TEAM_NOTEBOOK_REQUIRES_TEAM_PLAN')) {
    return 'Team-wide sharing requires a Team plan. Open Teams to upgrade your workspace.';
  }
  if (code.includes('ONLY_OWNER_CAN_SHARE')) {
    return 'Only the notebook owner can change sharing settings.';
  }
  if (code.includes('MEMBER_NOT_FOUND')) {
    return 'No account exists with that email. They need to sign up first.';
  }
  if (raw && String(raw).trim()) return String(raw).trim();
  return 'Could not update sharing.';
}

export function visibilityFromAccessMode(mode) {
  if (mode === NOTEBOOK_ACCESS.PUBLIC) return 'PUBLIC';
  if (mode === NOTEBOOK_ACCESS.TEAM) return 'TEAM';
  return 'PRIVATE';
}

/** POST /api/v1/learn/notebooks/{id}/share */
export async function applyNotebookSharing(apiClient, notebookId, mode, memberEmail) {
  if (!notebookId) return;
  if (mode === NOTEBOOK_ACCESS.PRIVATE || mode === NOTEBOOK_ACCESS.PUBLIC) {
    await apiClient.post(`/api/v1/learn/notebooks/${notebookId}/share`, {
      visibility: visibilityFromAccessMode(mode),
      role: 'VIEWER',
    });
    return;
  }
  const body = { role: 'VIEWER', visibility: visibilityFromAccessMode(mode) };
  if (mode === NOTEBOOK_ACCESS.EMAIL) {
    const email = (memberEmail || '').trim();
    if (!email) throw new Error('Email is required to invite a collaborator.');
    body.memberEmail = email;
    body.visibility = 'PRIVATE';
  }
  await apiClient.post(`/api/v1/learn/notebooks/${notebookId}/share`, body);
}

export function accessModeFromSummary(summary) {
  if (!summary) return NOTEBOOK_ACCESS.PUBLIC;
  const v = (summary.visibility || 'PUBLIC').toUpperCase();
  if (v === 'PUBLIC') return NOTEBOOK_ACCESS.PUBLIC;
  if (v === 'TEAM') return NOTEBOOK_ACCESS.TEAM;
  return NOTEBOOK_ACCESS.PRIVATE;
}
